import React, { useEffect, useRef, useState } from 'react'
import './secret-codes-form.styles.scss';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { ModalAlert, ModalConfirm } from '@/components/modal/modal.component';
import ManageContentInformation from '../blocks/information.component';
import ManageContentIconCropped from '../blocks/icon.component';
import ManageContentCategories from '../blocks/categories.component';
import ManageContentPrerequisites from '../blocks/prerequisites.component';
import ManageContentDates from '../blocks/dates.component';
import { useSelector } from 'react-redux';
import { getDownloadURL, getStorage, ref, uploadBytes } from 'firebase/storage';
import { UseCloudFunction } from '@/utils/firebase.utils';
import ThreeDotsLoader from '@/components/loader/three-dots-loader.component';
import ManageContentRewards from '../blocks/rewards.component';
import { membershipRestrictions, testMemberRestrictions } from '@/utils/membershipRestrictions';
import UpgradeRequiredModal from '@/components/SHARED/upgrade-required-modal/upgrade-required-modal.component';
import { RandomString } from '@/utils/randomString';
import ManageContentLimitations from '../blocks/limitations.component';
import BackButton from '@/components/SHARED/back-button/back-button.component';
import ImageContainer from '@/components/SHARED/image-container/image-container.component';
import ManageContentVisibility from '../blocks/visibility.component';
import ShowHideSettingsButton from '../blocks/show-hide-button.component';
import ElementDelete from '@/components/SHARED/element-delete/element-delete.component';
import useAnalytics from '@/utils/useAnalytics';
import { getOptimizedBlob } from '@/utils/getOptimizedBlob';

const SecretCodesForm = ({
  gameElements,
  secretCodesFromDb,
  gameData,
  gameStats,
  playerList
}) => {

  const membership = useSelector(state => state.userData.membership);
  const userProfile = useSelector(state => state.userData.userProfile);
  const screenSize = useSelector(state => state.contentData.screenSize);
  const [formData, setFormData] = useState({});
  const [availableItems, setAvailableItems] = useState(null);
  const [alertMessage, setAlertMessage] = useState(null);
  const [confirmMessage, setConfirmMessage] = useState(null);
  const [existingDescription, setExistingDescription] = useState(null);
  const [clearEditor, setClearEditor] = useState(false);
  const [categories, setCategories] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(null);
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const { secretCodePath } = useParams();
  const { trackEvent, trackException } = useAnalytics();
  const itemRef = useRef();
  const dataRef = useRef();
  const location = useLocation();
  const approveRef = useRef();
  const navigate = useNavigate();
  const storage = getStorage();

  useEffect(() => {
    if (!gameData || !membership || !location) return;
    if (location.pathname.includes('/elements/secretcodes/edit')) return;
    const res = testMemberRestrictions({ 'gameData': gameData, 'membership': membership, 'create': true });
    if (res.disabled && res.disabledReason.includes('elements')) {
      const planRest = membershipRestrictions[membership.membership];
      const message = `While using the ${planRest.title}, you are only able to have ${planRest.elementCount} active Game Elements per game.`;
      setShowUpgradeModal(message);
      return;
    }
  }, [gameData, membership, location])

  useEffect(() => {
    if (!formData) { return; }
    if (!formData.icon) {
      // dataRef.current = {...dataRef.current, ...{'icon': {'url': '/images/icons/secret.png'}}};
      // setFormData(dataRef.current);
      saveData('icon', '/images/icons/secret.png')
    }
  }, [formData])

  useEffect(() => {
    if (!secretCodePath || !secretCodesFromDb) { return; }
    const existing = Object.values(secretCodesFromDb).filter(e => e.path === secretCodePath)[0];
    dataRef.current = {...existing};
    setFormData(dataRef.current);
    if (dataRef.current?.desc) {
      setExistingDescription(dataRef.current.desc);
    }
  }, [secretCodePath, secretCodesFromDb])

  useEffect(() => {
    if (!secretCodesFromDb) { return; }
    let arr = [];
    for (let c of Object.values(secretCodesFromDb).filter(c => c.type === 'secretCode')) {
      if (c.opt_cat && !arr.includes(c.opt_cat)) {
        arr.push(c.opt_cat)
      }
    }
    setCategories(arr.sort())
    let itemObj = {};
    Object.values(secretCodesFromDb).filter(e => e.opt_itemType === 'inventory').map(e => itemObj[e.id] = e)
    console.log(itemObj);
    setAvailableItems({ ...itemObj });
  }, [secretCodesFromDb])

  useEffect(() => {
    if (!playerList) return;
    const teamArr = Object.values(playerList)
      .filter(p => p.team);
    setTeams(teamArr);
  }, [playerList])

  function saveData(key, value) {
    if (key === 'name') {
      value = value.slice(0, 50);
    }
    let tempData = { ...dataRef.current };
    if (!value) {
      delete tempData[key];
      dataRef.current = { ...tempData };
    } else {
      dataRef.current = { ...dataRef.current, ...{ [key]: value } };
    }
    setFormData(dataRef.current);
  }

  function cancelCreate() {
    setConfirmMessage('Are you sure you want to cancel? All of your data will be lost.');
    approveRef.current = () => {
      dataRef.current = { 'icon': { 'url': '/images/icons/secret.png' } };
      setFormData(dataRef.current);
      navigate(-1)
    }
  }

  async function handleSubmit() {
    if (location.pathname.includes('/preview/manager/')) {
      setAlertMessage('Sorry! Data cannot be saved in Preview Mode.');
      return;
    }
    let tempData = { ...dataRef.current }
    const reqs = ['name', 'icon']
    for (let r of reqs) {
      if (!tempData[r]) {
        setAlertMessage('Please include a name and an icon for this secret code before saving.');
        return;
      }
    }
    if (
      gameElements &&
      Object.values(gameElements).filter(e =>
        e.name.trim().toLowerCase() === tempData.name.trim().toLowerCase() &&
        e.id !== tempData.id
      ).length > 0
    ) {
      setAlertMessage('There is already a Secret Code with this name/code. ' +
        'Please choose a different name for this secret code.');
      setLoading(false);
      return;
    }
    // check for some kind of reward
    if (
      !tempData.opt_earnedPointsMin &&
      !tempData.opt_earnedPointsMax &&
      !tempData.opt_earnedCurrencyMin &&
      !tempData.opt_earnedCurrencyMax &&
      !tempData.opt_earnedBadges?.length &&
      !tempData.opt_earnedItems?.length &&
      !tempData.opt_earnedPrizePacks?.length
    ) {
      setAlertMessage('Please include at least one reward for players to receive when redeeming this secret code.');
      setLoading(false);
      return;
    }
    setConfirmMessage('Are you sure you are ready to save this secret code? All of these settings can be changed later.')
    approveRef.current = () => {
      finishApproval()
    }
  }

  async function finishApproval() {
    setLoading(true);
    let tempData = { ...dataRef.current }
    console.log(tempData);
    if (tempData.icon.upload) {
      // tempData.icon.croppie.result({
      // 	type:'blob',
      // 	size: {'width': 352, 'height': 352},
      //     quality: 0.7,
      // })
      // .then((blob) => {
      // 	uploadImage(tempData.icon, blob)
      // 	return;
      // })
      const optimizedBlob = await getOptimizedBlob(tempData.icon.croppie, 25);
      console.log('Cropped image size in KB:', optimizedBlob.size / 1024);
      uploadImage(tempData.icon, optimizedBlob);
    } else if (tempData.icon.url) {
      saveSecretCode(tempData.icon.url)
      return;
    } else if (tempData.icon) {
      saveSecretCode(tempData.icon)
      return;
    } else {
      setAlertMessage('Something went wrong with the icon. Please try using a different image.');
      setLoading(false);
      return;
    }
  }

  function uploadImage(icon, blob) {
    const now = new Date().getTime();
    const path = `${userProfile.appId}/${now}-${icon.name}`;
    const iconRef = ref(storage, path)
    const metadata = {
      contentType: icon.type,
      customMetadata: {
        appId: userProfile.appId,
        email: userProfile.email
      }
    };
    uploadBytes(iconRef, blob, metadata).then((snapshot) => {
      getDownloadURL(iconRef)
        .then(async (url) => {
          saveSecretCode(url);
          try {
            await UseCloudFunction(
              'saveToMyContent',
              {
                'gameId': gameData.gameId,
                'appId': userProfile.appId,
                'source': 'Secret Code Icons',
                'category': 'Images',
                'cropped': true,
                'contentData': {
                  'name': icon.name,
                  'url': url,
                  'type': 'image',
                  'id': `${now}_${RandomString(10)}_image`
                },
              }
            )
          } catch (error) {
            console.error('Error saving to my content:', error);
            setAlertMessage('Something went wrong while saving the icon to My Content. Error: ' + error.message);
            trackException(`Save secret code icon to My Content Error: ${error}`);
          }
          trackEvent(
            'image_saved_to_my_content',
            'Game Manager',
            `Secret Code icon ${icon.name} was saved to My Content for user ${userProfile.appId}.`,
            null,
            membership
          )
          return;
        })
    })
  }

  async function saveSecretCode(iconUrl) {
    let tempData = { ...dataRef.current }
    console.log(tempData);
    const now = new Date().getTime();
    const gameCode = gameData.gameId.substring(gameData.gameId.indexOf('-') - 5, gameData.gameId.indexOf('-') + 6)
    let action = 'Edited';
    if (!tempData.id) {
      tempData.appId = userProfile.appId;
      tempData.gameId = gameData.gameId;
      tempData.id = now + '-' + gameCode + '-secretCode';
      tempData.status = 'active';
      tempData.type = 'secretCode';
      tempData.opt_visible = true;
      tempData.path = RandomString(18, 'abcdefghijklmnopqrstuvwxyz-_0123456789');
      tempData.opt_available = true;
      action = 'Created';
    }
    tempData.icon = iconUrl;
    tempData.code = tempData.name.toUpperCase().replace(/\s/gi, '');
    tempData.opt_code = tempData.code;
    console.log(tempData);
    const res = await UseCloudFunction(
      'saveGameElement',
      {
        'gameId': gameData.gameId,
        'appId': userProfile.appId,
        'elementData': tempData,
        'userName': `${userProfile.fName} ${userProfile.lName}`,
        'action': action
      })
    console.log(res);
    if (res.error) {
      setAlertMessage('Something went wrong. Error: ' + res.error);
      setLoading(false);
      trackException(`Secret Code ${action} Error: ${res.error}`);
      return;
    }
    setAlertMessage('Secret Code saved successfully.');
    dataRef.current = {};
    setFormData(dataRef.current);
    setLoading(false);
    const event = action === 'Created' ? 'secret_code_created' : 'secret_code_edited';
    trackEvent(
      event,
      'Game Manager',
      `Secret Code ${tempData.id} was ${action.toLowerCase()}.`,
      null,
      membership
    )
    navigate(-1);
  }

  return (
    <div className={`create-secret-code-form ${screenSize}`}>
      <div className='form-grid'>
        <div className='g-card secret-codes-card'>
          <BackButton cancel={() => navigate(-1)} />
          <div className="g-space-1"></div>
          <div className='card-title'>
            <ImageContainer src='/images/icons/secret2.png' alt='secret codes icon' className='head-icon' />
            Create and Edit Secret Codes
          </div>
          <hr />
          <span className='required meta'>* = Required</span>
          <div className="g-space-0-5"></div>
          <ManageContentInformation
            type='secretCode'
            saveData={saveData}
            formData={formData}
            existingDescription={existingDescription}
            clearEditor={clearEditor}
            setClearEditor={setClearEditor}
            textEditorLabel='Secret Code Description (optional)'
          />
          <div className='g-space-2'></div>
          <ManageContentRewards
            type='secretCode'
            saveData={saveData}
            formData={formData}
            gameElements={gameElements}
            gameStats={gameStats}
            text='finding this Secret Code'
            gameData={gameData}
          />
          <div className='g-space-1'></div>
          <ShowHideSettingsButton
            setShowAdvancedOptions={setShowAdvancedOptions}
            showAdvancedOptions={showAdvancedOptions} />
          <div className={`advanced-options ${showAdvancedOptions ? 'show' : 'hide'}`}>
            <div className='g-space-1'></div>
            <ManageContentIconCropped
              type='secretCode'
              saveData={saveData}
              formData={formData}
            />
            <div className='g-space-2'></div>
            <ManageContentCategories
              type='secretCode'
              saveData={saveData}
              formData={formData}
              categories={categories}
            />
            <div className='g-space-2'></div>
            <ManageContentVisibility
              type='secretCode'
              saveData={saveData}
              formData={formData}
              visibilityText="Turn visibility ON for this Secret Code. If toggled off, players can not redeem it, nor will it appear in their history."
              availabilityText="This Secret Code is able to be redeemed by players if it is open according to the dates below and if the player meets any chosen prerequisites. If toggled off, players cannot redeem this Secret Code."
            />
            <div className='g-space-2'></div>
            <ManageContentLimitations
              type='secretCode'
              saveData={saveData}
              formData={formData}
            />
            <div className='g-space-2'></div>
            <ManageContentPrerequisites
              type='secretCode'
              saveData={saveData}
              formData={formData}
              gameData={gameData}
              gameStats={gameStats}
              gameElements={gameElements}
              availableItems={availableItems}
              teams={teams}
            />

            <div className='g-space-2'></div>
            <ManageContentDates
              type='secretCode'
              saveData={saveData}
              formData={formData}
              gameElements={gameElements}
              text='Secret Codes will NOT be available to be earned before or after these dates.'
            />
            <div className="g-space-1"></div>
            <ShowHideSettingsButton
              showAdvancedOptions={showAdvancedOptions}
              setShowAdvancedOptions={setShowAdvancedOptions}
              below={true} />
          </div>
          <div className="g-space-0-5"></div>
          <div className='buttons'>
            {
              (loading)
                ?
                <>
                  {
                    (formData.ts_created) &&
                    <button
                      type='button'
                      className={`g-button small-btn red ${screenSize !== 'mobile' ? 'left-item' : ''}`}>
                      Delete
                    </button>
                  }
                  <button
                    type='button'
                    className={`g-button ${screenSize === 'mobile' ? 'med-btn' : ''}`}>
                    Cancel
                  </button>
                  <button
                    type='button'
                    className={`g-button ${screenSize === 'mobile' ? 'med-btn' : ''}`}>
                    <ThreeDotsLoader />
                  </button>
                </>
                :
                <>
                  {
                    (formData.ts_created) &&
                    <ElementDelete
                      element={formData}
                      style='button'
                      classText={screenSize !== 'mobile' ? 'left-item' : ''} />
                  }
                  <button
                    type='button'
                    className={`g-button ${screenSize === 'mobile' ? 'med-btn' : ''}`}
                    onClick={() => cancelCreate()}>
                    Cancel
                  </button>
                  <button
                    type='button'
                    className={`g-button submit-button`}
                    onClick={() => handleSubmit()}>
                    Save Secret Code
                  </button>
                </>
            }
          </div>
        </div>
        {
          (screenSize !== 'mobile' && screenSize !== 'tablet') &&
          <div >
            <div className='g-card fixed-card'>
              <button
                className='g-button med-btn submit-button'
                onClick={() => handleSubmit()}
              >
                Save Secret Code
              </button>
            </div>
          </div>
        }
      </div>
      <ModalAlert show={alertMessage} cancel={() => setAlertMessage(null)} message={alertMessage} />
      <ModalConfirm show={confirmMessage} cancel={() => setConfirmMessage(null)} message={confirmMessage} onApprove={approveRef.current} />
      <UpgradeRequiredModal
        show={showUpgradeModal}
        cancel={() => setShowUpgradeModal(null)}
        message={showUpgradeModal}
      />
    </div>
  )
}

export default SecretCodesForm